import React, { useState, useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import MuiLink from "@mui/material/Link";
import Loader from "components/Progress";
import Card from "@mui/material/Card";
import Table from "components/Table/newTable";
import { CardContent } from "@mui/material";
import { useGetAlarmLogQuery } from "../../../services/alarms";
import { alarmsQuery } from "../List";
import {
  timeLabel,
  ruleLabel,
  valueLabel,
  problemLabel,
  notifyLabels,
} from "../logLabels";

// The alarm log keeps 31 days, so the timeline never asks for older entries.
const LOG_DAYS = 31;

// "Priority" is the label newTable gives the coloured pill
const columns = [
  { id: "service", label: "Solution", align: "center" },
  { id: "deviceName", label: "Asset", align: "center" },
  { id: "severity", label: "Priority", align: "center" },
  { id: "rule", label: "Rule", align: "center" },
  { id: "value", label: "Value", align: "center" },
  { id: "problem", label: "Problem", align: "center" },
  { id: "notify", label: "E-mail", align: "center" },
  { id: "time", label: "Time", align: "center" },
];

export default function Timeline({ serviceId, viewToggle, fill }) {
  let token = window.localStorage.getItem("token");
  const { enqueueSnackbar } = useSnackbar();
  const alarmsFilter = useSelector((state) => state.alarmsFilter);
  const metaDataValue = useSelector((state) => state.metaData);
  const [page, setPage] = useState(1);

  // The request starts no earlier than 31 days ago. Worked out once per
  // chosen range, so the request stays the same between renders.
  const { dateFrom, olderThanLog, outsideLog } = useMemo(() => {
    const earliest = new Date().setDate(new Date().getDate() - LOG_DAYS);
    const chosen = new Date(alarmsFilter.date.startTime).setSeconds(0);
    const from = Math.max(chosen, earliest);
    return {
      dateFrom: new Date(from).toISOString(),
      olderThanLog: chosen < earliest,
      // the whole chosen range is older than the log keeps: nothing to ask
      // for, the note shows and no rows (code review 2026-10-30)
      outsideLog: from >= new Date(alarmsFilter.date.endTime).getTime(),
    };
  }, [alarmsFilter.date.startTime, alarmsFilter.date.endTime]);

  // Same filters and encoding as the List, less status, e-mail and actuation
  // (the log has no such fields).
  const filterParams = alarmsQuery(alarmsFilter, {
    serviceId,
    services: metaDataValue.services,
    withStatus: false,
    dates: { startTime: dateFrom, endTime: alarmsFilter.date.endTime },
  });
  // Back to page 1 when the request changes; keyed on the request, not the
  // whole filter, so a change that asks for nothing new keeps the page
  // (code review 2026-10-30).
  useEffect(() => {
    setPage(1);
  }, [filterParams]);
  const log = useGetAlarmLogQuery(
    {
      token,
      params: `?${filterParams}&pageSize=10&withTotalPages=true&currentPage=${page}`,
    },
    { skip: outsideLog }
  );

  useEffect(() => {
    if (!log.isFetching && log.isError) {
      enqueueSnackbar(
        {
          title: "Alarms",
          message: log.error?.data?.message || "Could not load the timeline",
          variant: "error",
        },
        { timeOut: 1000 }
      );
    }
  }, [log.isFetching]);

  // currentData: this request's result only, so a failed load never shows the previous rows
  const totalPages = log.currentData?.payload?.totalPages || 0;
  const totalDocuments = log.currentData?.payload?.totalDocuments || 0;
  const entries = log.currentData?.payload?.data;
  const rows = useMemo(
    () =>
      (entries || []).map((entry) => {
        const service = metaDataValue.services.find(
          (s) => s.id == entry.serviceId
        );
        return {
          id: entry._id,
          service: service?.name || "",
          // linked into the device's Asset View only when the solution is
          // known, styled as the List's asset link
          deviceName:
            service && entry.sensorId && entry.deviceName ? (
              <MuiLink
                component={RouterLink}
                to={`/solutions/${entry.serviceId}/${entry.sensorId}/0`}
                underline="hover"
                title={`Open ${entry.deviceName}`}
                sx={{
                  color: metaDataValue.branding.primaryColor,
                  fontWeight: 500,
                }}
              >
                {entry.deviceName}
              </MuiLink>
            ) : (
              entry.deviceName
            ),
          severity: entry.severity,
          rule: ruleLabel(entry.type),
          value: valueLabel(entry),
          problem: problemLabel(entry),
          notify: notifyLabels[entry.notify] || "",
          time: timeLabel(entry.t),
        };
      }),
    [entries, metaDataValue.services, metaDataValue.branding.primaryColor]
  );

  return (
    // fill: the card and its content are flex columns taking the area's height,
    // so the table scrolls inside and the pager stays in view
    <Card
      sx={{
        borderRadius: "10px",
        height: "100%",
        ...(fill ? { display: "flex", flexDirection: "column" } : {}),
      }}
    >
      <CardContent
        sx={{
          padding: "0px",
          ...(fill
            ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
            : {}),
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
            padding: "16px",
          }}
        >
          <p
            style={{
              color: "#bfbec8",
              fontSize: "15px",
              fontWeight: "bold",
            }}
          >
            Timeline
            {!log.isFetching && !log.isError ? (
              <span style={{ fontWeight: "normal" }}>
                {` ( ${totalDocuments} alarm ${
                  totalDocuments == 1 ? "raise" : "raises"
                } )`}
              </span>
            ) : null}
          </p>
          {viewToggle}
          {/* the chosen start is older than the log keeps */}
          <p
            style={{
              color: "#9f9f9f",
              fontSize: "12px",
              textAlign: "right",
            }}
          >
            {olderThanLog
              ? "The timeline covers the last 31 days."
              : !log.isFetching && !log.isError && !totalDocuments
              ? "Raises are recorded since the alarm log was switched on and kept 31 days."
              : ""}
          </p>
        </div>
        {log.isFetching ? (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Loader />
          </span>
        ) : (
          <Table
            columns={columns}
            rows={rows}
            page={page}
            totalPages={totalPages}
            totalDocuments={totalDocuments}
            handleChange={setPage}
            height={fill ? undefined : "calc(100vh - 491px)"}
            fill={fill}
            pageSize={10}
            size="small"
            minHeight={"auto"}
          />
        )}
      </CardContent>
    </Card>
  );
}
